import { jsx as _jsx } from "react/jsx-runtime";
/**
 * InlineAnnotationsLayer — paints the `text_range`-anchored annotation
 * types as `mix-blend-mode: multiply` rects over the host text.
 *
 * This is a deliberate sibling of `AnnotationViewContainer`: the
 * container's own `transform: scale(--annotation-natural-scale)`
 * creates a stacking context AND auto-isolates it for compositing
 * (CSS Compositing 1 §"Auto Isolation" — `transform`, `opacity < 1`,
 * `filter`, `mask`, etc. all force isolation when they create a
 * stacking context). Inside that isolated group `mix-blend-mode`
 * rects can no longer see the painted card text as backdrop, so
 * the blend silently degrades to a flat opaque tile. The container
 * legitimately needs the transform — it's the single coordinate
 * frame the FLIP grow rides on, shared across stamps / drawings /
 * stickers / pins. The blend rects are the exception that has to
 * live elsewhere.
 *
 * "Elsewhere" = a sibling of `.block-view` (stream cards) /
 * `<BlockView>` (modal panel) that is **not** itself a stacking
 * context. The wrapper inherits the parent's stacking context,
 * which is also where the card content paints, so the rects'
 * `mix-blend-mode: multiply` blends against the painted card text
 * the way it was always supposed to.
 *
 * Two annotation types render here:
 *   - `text_highlight` — colored rects across the matched range
 *   - `comment` with `anchor.type === "text_range"` — yellow rects
 *     plus a tap-to-popover affordance (touch only)
 *
 * Reply comments (`parentId !== undefined`) don't get their own
 * anchored surface — they're part of the parent's thread, surfaced
 * inside the popover / rail. Filtered here for parity with the
 * matching skip in `CommentAnnotationView`.
 *
 * Subscription is `userAnnotationStore.useByBlock(blockId)`, the
 * same query `AnnotationViewContainer` runs. TanStack DB dedupes
 * the underlying cursor across subscribers, so the second hook is
 * essentially free.
 */
import { memo, useRef } from "react";
import { userAnnotationStore } from "@/data/user-annotation-store";
import { TextHighlightAnnotationView } from "./views/text-highlight-annotation-view";
import { CommentTextRangeView } from "./views/comment-annotation-view";
import "./annotation.scss";
export const InlineAnnotationsLayer = memo(function InlineAnnotationsLayer({ blockId, mode, highlightSelection, }) {
    // The layer's own div is the rect-origin every per-line rect
    // resolves against. Sized `inset: 0` of its parent (the card host
    // / modal panel), so its `getBoundingClientRect()` matches the
    // text host's visual rect and the rects from `useTextRangeRects`
    // land at the right visual pixels without any scale compensation.
    const containerRef = useRef(null);
    const { data: annotations } = userAnnotationStore.useByBlock(blockId);
    return (_jsx("div", { ref: containerRef, className: `annotations-inline-layer annotations-inline-layer--mode-${mode}`, "aria-hidden": true, children: annotations.map((annotation) => {
            if (annotation.type === "text_highlight") {
                // Per-highlight selection prop. Only wire when the
                // caller passed a selection bundle AND we're in expanded
                // mode — stream cards keep highlights non-interactive.
                const selectionProp = mode === "expanded" && highlightSelection !== undefined
                    ? {
                        isSelected: highlightSelection.selectedId ===
                            annotation.id,
                        onSelect: highlightSelection.onSelect,
                    }
                    : undefined;
                return (_jsx(TextHighlightAnnotationView, { annotation: annotation, mode: mode, containerRef: containerRef, selection: selectionProp }, annotation.id));
            }
            if (annotation.type === "comment" &&
                annotation.parentId === undefined &&
                annotation.anchor?.type === "text_range") {
                return (_jsx(CommentTextRangeView, { annotation: annotation, mode: mode, containerRef: containerRef }, annotation.id));
            }
            return null;
        }) }));
});
